import { apiClient } from "../../lib/apiClient";
import type {
  ContextResponse,
  GuildOption,
  PlayerChatSummary,
  StreamFilters,
  StreamResponse,
} from "./types";

const base = (projectId: string) =>
  `/api/admin/projects/${encodeURIComponent(projectId)}/chat`;

/** A keyset position in the stream: `<created_at>,<id>` of a row. */
export function cursorOf(row: { created_at: string; id: string }): string {
  return `${row.created_at},${row.id}`;
}

export interface StreamPageParams {
  /** Rows strictly older than this cursor. */
  before?: string;
  limit?: number;
}

/** One page of the stream, newest first. */
export async function listStream(
  projectId: string,
  filters: StreamFilters,
  page: StreamPageParams = {},
): Promise<StreamResponse> {
  const qs = new URLSearchParams();
  if (filters.q) qs.set("q", filters.q);
  if (filters.guild) qs.set("guild", filters.guild);
  if (filters.player) qs.set("player", filters.player);
  if (page.before) qs.set("before", page.before);
  if (page.limit) qs.set("limit", String(page.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const r = await apiClient.get<StreamResponse>(
    `${base(projectId)}/messages${suffix}`,
  );
  return r.data;
}

/**
 * The messages around one message in its guild, oldest first. `at` is the
 * message's `created_at` when known — it lets the server find it by key.
 */
export async function getContext(
  projectId: string,
  messageId: string,
  before: number,
  after: number,
  at?: string,
): Promise<ContextResponse> {
  const qs = new URLSearchParams({ before: String(before), after: String(after) });
  if (at) qs.set("at", at);
  const r = await apiClient.get<ContextResponse>(
    `${base(projectId)}/messages/${encodeURIComponent(messageId)}/context?${qs.toString()}`,
  );
  return r.data;
}

/** How much one player wrote in the retained history, and where. */
export async function getPlayerSummary(
  projectId: string,
  playerId: string,
): Promise<PlayerChatSummary> {
  const r = await apiClient.get<PlayerChatSummary>(
    `${base(projectId)}/players/${encodeURIComponent(playerId)}`,
  );
  return r.data;
}

/** Every guild of the project, for the guild filter. */
export async function listGuildOptions(
  projectId: string,
): Promise<GuildOption[]> {
  const r = await apiClient.get<{ items: GuildOption[] }>(
    `${base(projectId)}/guilds`,
  );
  return r.data.items;
}
